import type { ParsedUrlQuery } from "querystring";

import { isUsLocale } from "@/i18n/config";

/**
 * WHO-6510. On `/en-us` every list (search, category and landing pages, their "New this week",
 * the homepage's) shows only listings that ship to the US, by default. The search service filters
 * on the indexed `us_shippable` term (WHO-6506); whether a listing ships is decided once, in the
 * dashboard (WHO-6498).
 *
 * The default is applied where a URL turns into the API call (`resolveSearchRoute`), on the server
 * as on the client, so Google is served the same `/en-us` list a US visitor sees and hydration
 * never refetches. The shopper's way out is `allItems=1` in the URL, set by the "Ships to the US"
 * chip: a page of its own, never indexed (any filter param is noindex). A `us_shippable` in an
 * `/en-us` URL is ignored, so the chip is the one switch and a hand-built URL cannot leave a
 * filter the page doesn't show.
 *
 * Every other locale is untouched. `/en-us` itself is only routed while `US_LAUNCH_ENABLED` is on.
 */
export const US_SHIPPABLE_SEARCH_PARAM = "us_shippable";
export const US_SHIPPABLE_SEARCH_VALUE = "true";

/** URL-only: never sent to the search service (`paramsFromQuery` drops it). */
export const US_ALL_ITEMS_URL_PARAM = "allItems";
export const US_ALL_ITEMS_URL_VALUE = "1";

export const isAllItemsInUrl = (query: ParsedUrlQuery): boolean =>
  query[US_ALL_ITEMS_URL_PARAM] === US_ALL_ITEMS_URL_VALUE;

/**
 * The `$usMarket` variable for gateway queries that select `usShippable @include(if: $usMarket)`:
 * set on `/en-us` only, so every other locale sends exactly the variables it did before.
 */
export const usMarketVariables = (
  locale: string | undefined
): { usMarket?: true } => (isUsLocale(locale) ? { usMarket: true } : {});

/** Whether `/en-us`'s default filter applies to this URL: the US locale, without `allItems=1`. */
export const isUsShippableDefaultOn = (
  query: ParsedUrlQuery,
  locale: string | undefined
): boolean => isUsLocale(locale) && !isAllItemsInUrl(query);

/**
 * The search params for `locale` with the US default applied: `us_shippable=true` on `/en-us`
 * unless the URL asks for all items. Returns `params` itself everywhere else.
 */
export const withUsShippableDefault = <T extends object>(
  params: T,
  query: ParsedUrlQuery,
  locale: string | undefined
): T => {
  if (!isUsLocale(locale)) {
    return params;
  }

  const { [US_SHIPPABLE_SEARCH_PARAM]: _ignored, ...rest } = params as Record<
    string,
    unknown
  >;

  return (
    isAllItemsInUrl(query)
      ? rest
      : { ...rest, [US_SHIPPABLE_SEARCH_PARAM]: [US_SHIPPABLE_SEARCH_VALUE] }
  ) as T;
};
